import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { AuthenticationService } from '../../../../core/services/authentication.service';
import { OauthProvider } from '../../../../oauth/providers';
import { LoadingService, RequestBodyService } from '../../../services';
import { CommonUtilService } from '../../../services/CommonUtil.service';
import { ReConnectSettingComponent } from '../../re-connect-setting/re-connect-setting.component';
import { AccountAttributesEnum, OfflineExportsCrmExportTypesEnum, OfflineExportsExportTypesEnum } from '../../../enums';
import {
  HubspotCompany,
  HubspotCompanyDuplicate,
  HubspotCompanyExport,
  HubspotCompanyRequest,
  HubspotDto,
  HubspotExport,
  HubspotWorkflowDto,
  LifecycleStageDto,
  OfflineExportsCrmExportRequestDto,
} from '../../../dto';
import { HubSpotExportType } from 'src/app/shared/dto/different-interfaces';
import { forkJoin, Observable } from 'rxjs';
import { UserService } from '../../../../core/services/user.service';
import { OutreachScopeModalComponent } from '../../outreach-scope-modal/outreach-scope-modal.component';
import { HubspotApiService, OfflineExportsApiService } from '../../../services/api';
import { environment } from '../../../../../environments/environment';
import { getErrorMessage } from 'src/app/shared/helpers/get-error-message.helper';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SiModalModule } from '../../si-modal';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { FilterPipeModule, OrderByPipeModule } from '../../../pipes';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { FilterExportTypesPipe } from './pipes/filter-export-types.pipe';
import {
  DuplicateResolutionRadioGroupModule
} from '../../ui-components/duplicate-resolution-group/duplicate-resolution-radio-group.module';

enum PageTypes {
    EXPORT_TYPE = "exportType",
    LEAD_OWNER = "leadOwner",
    DUPLICATES = "duplicates",
    DUPLICATES_ASYNC = "duplicatesAsync",
    JOB_TITLE = "jobTitle",
    EXPORT = "export",
    EXPORT_ASYNC = "exportAsync",
    EXPORT_FINISHED = "exportFinished",
    EXPORT_EMPTY = "exportEmpty",
    EXPORT_FAILED = "exportFailed",
    REQUIRES_REAUTH = "requiresReauth",
    WORKFLOW = "workflow",
}

const LOADING_TAG_ASYNC_EXPORT: string = 'app-hubspot-large-export-loading-tag';

@UntilDestroy()
@Component({
  selector: 'app-hubspot',
  templateUrl: './hubspot.component.html',
  styleUrls: ['./hubspot.component.scss'],
  imports: [
    CommonModule,
    SiModalModule,
    MatFormFieldModule,
    FontAwesomeModule,
    FormsModule,
    ReactiveFormsModule,
    DuplicateResolutionRadioGroupModule,
    FilterPipeModule,
    OrderByPipeModule,
    FilterExportTypesPipe,
    MatInputModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatButtonModule
  ],
  standalone: true,
})
export class HubspotComponent implements OnInit {
    readonly isLoading$: Observable<boolean> = this.loadingService.getIsLoading(LOADING_TAG_ASYNC_EXPORT);
    pages: { type: PageTypes }[];
    exportRecords: HubspotExport[] = [];
    icpIntelJobUUID: string = null;
    retryExports = false;
    showRetryButton = false;
    exportedContactCount: number = 0;
    exportedCompanyCount: number = 0;
    term;
    warnings = [];
    isShowingWarnings = false;

    leadOwners = [];
    leadOwnerFilter = '';
    leadOwnersLoaded = false;
    selectedLeadOwner: string = '';

    selectedWorkflows = [];
    workflows: HubspotWorkflowDto[] = [];
    workFlowFilter = '';

    busyLabel = 'Loading ...';
    busySublabel = '';
    isBusy = true;

    duplicateCompanyMatches: Array<HubspotCompany> = [];
    duplicateContactMatches: Array<HubspotDto> = [];

    duplicateCount = 0;
    duplicateResolution = 'update';
    exportType: string = null;
    exportTypesSearchable: { [key: string]: string } = {};
    showPreviousButton = false;
    showNextButton = false;
    showCancelButton = true;
    showCloseButton = false;
    showExportTypeBox = true;
    currentPageIndex: number;
    initialPageIndex: number;
    exportError = '';
    settings: any;
    isHubSpotComponentOpened: boolean = true;
    accuracyTier: Array<'HV' | 'EV' | 'MV'> | null;
    accuracyTierText: string;
    exportTypesHubSpot: Array<HubSpotExportType>;
    exportTypeFilter: string = '';
    readonly jobTitleControl: FormControl = new FormControl();
    readonly accountAttributesEnum: typeof AccountAttributesEnum = AccountAttributesEnum;
    readonly pageTypes: typeof PageTypes = PageTypes;

    private readonly asyncInitialData: {
        request_type: OfflineExportsExportTypesEnum;
        totalCount: number;
        requestBody: any;
    };

    constructor(
        private commonUtilService: CommonUtilService,
        private hubspotApiService: HubspotApiService,
        private authenticationService: AuthenticationService,
        private requestBodyService: RequestBodyService,
        private readonly userService: UserService,
        private readonly matDialog: MatDialog,
        private readonly matDialogRef: MatDialogRef<HubspotComponent>,
        private readonly loadingService: LoadingService,
        private readonly offlineExportsApiService: OfflineExportsApiService,
        @Inject(MAT_DIALOG_DATA) public readonly data: any,
    ) {

        this.icpIntelJobUUID = this.data['icpIntelJobUUID'] || null;

        if (this.data.requestBody) {
            this.asyncInitialData = { ...this.data };
        }

        if (!data.company) {
            this.accuracyTier = this.commonUtilService.getSelectedAccuracyTier(
                this.data.contacts,
                this.data.requestBody
            );
            this.accuracyTierText = this.commonUtilService.getAccuracyTierExportText(this.accuracyTier);
        }

        if (this.data['contacts']) {
            this.exportRecords = this.data['contacts'];
            // this.accuracyTier = this.data['accuracyTier'];
            // this.accuracyTierText = this.data['accuracyTierText'];
        }
        if (this.data['company']) {
            this.exportRecords = this.data['company'];
        }
    }
    ngOnInit() {
        if (this.data['contacts'] && this.data.contacts.length <= environment.hubSpotMaxCount) {
            this.pages = [
                { type: PageTypes.EXPORT_TYPE },
                { type: PageTypes.LEAD_OWNER },
                { type: PageTypes.WORKFLOW },
                { type: PageTypes.DUPLICATES },
                { type: PageTypes.EXPORT },
                { type: PageTypes.EXPORT_FINISHED },
                { type: PageTypes.EXPORT_EMPTY },
                { type: PageTypes.EXPORT_FAILED },
                { type: PageTypes.REQUIRES_REAUTH },
            ]
            this.initialPageIndex = this.getPageIndex(PageTypes.EXPORT_TYPE);
        }
        if ((this.data.requestBody && this.data.request_type === OfflineExportsExportTypesEnum.CONTACTS) || (this.data.contacts && this.data.contacts.length > environment.hubSpotMaxCount)) {
            this.pages = [
                { type: PageTypes.EXPORT_TYPE },
                { type: PageTypes.LEAD_OWNER },
                { type: PageTypes.DUPLICATES_ASYNC },
                { type: PageTypes.JOB_TITLE },
                { type: PageTypes.EXPORT_ASYNC },
                { type: PageTypes.EXPORT_FAILED },
            ];
            this.initialPageIndex = this.getPageIndex(PageTypes.EXPORT_TYPE);
        }
        if (this.data['company']) {
            this.pages = [
                { type: PageTypes.DUPLICATES },
                { type: PageTypes.EXPORT },
                { type: PageTypes.EXPORT_FINISHED },
                { type: PageTypes.EXPORT_EMPTY },
                { type: PageTypes.EXPORT_FAILED },
                { type: PageTypes.REQUIRES_REAUTH },
            ]
            this.initialPageIndex = this.getPageIndex(PageTypes.DUPLICATES);
            this.currentPageIndex = this.getPageIndex(PageTypes.DUPLICATES);
        }
        forkJoin([
            this.hubspotApiService.getSettings(),
            this.hubspotApiService.getLifecycleStages(),
            this.hubspotApiService.getScopes(),
        ]).pipe(untilDestroyed(this)).subscribe(([data, lifecycleStages, scopesResponse]) => {
            if (data.general?.hsCrmStatus && !this.areNewScopesPresent(scopesResponse)) {
                this.checkVerification('general.hsCrmStatus');
                return;
            }
            this.settings = data;
            this.exportTypesHubSpot = lifecycleStages.map((item: LifecycleStageDto) => {
                this.exportTypesSearchable[item.value] = item.label;
                return { id: item.value, type: item.label };
            });
            if (this.settings.general.hsDuplicateResolution === 'prompt') {
                this.duplicateResolution = 'update';
            } else {
                this.duplicateResolution = this.settings.general.hsDuplicateResolution;
            }

            if (this.data['contacts'] || (this.data.requestBody && this.data.request_type === OfflineExportsExportTypesEnum.CONTACTS)) {
                if (this.settings.general.hsExportType !== 'prompt') {
                    this.exportType = this.settings.general.hsExportType;
                    this.showExportTypeBox = false;
                    this.currentPageIndex = this.getPageIndex(PageTypes.LEAD_OWNER);
                    this.showNextButton = true;
                } else {
                    // this.showExportTypeBox = true;
                    this.currentPageIndex = this.getPageIndex(PageTypes.EXPORT_TYPE);
                }
            }

            this.initializeFirstStep();
        }, (error) => {
            if (error.status === 404 && error.error?.message) {
                this.commonUtilService.showToastWithError(getErrorMessage(error));
                this.onClose();
                return;
            }
            this.hubspotApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(() => {
                this.ngOnInit();
            }, () => {
                this.errorRefreshToken();
            });
        });
    }

    areNewScopesPresent(scopesResponse) {
        return scopesResponse.scopes.includes('crm.schemas.companies.read') &&
            scopesResponse.scopes.includes('crm.objects.companies.write')
    }

    private checkVerification(attribute: string): void {
        const initialState: MatDialogConfig = {
            data: { attribute, provider: 'Hubspot', text: 'In order to export to HubSpot you will need to log back into your HubSpot account.', isExport: true }
        };
        this.matDialog.open(OutreachScopeModalComponent, initialState).afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.onClose();
            });
    }


    setExportType(type: string): void {
        if (this.exportType === type) {
            this.exportType = null;
            this.showNextButton = false;
        } else {
            this.exportType = type;
            this.showNextButton = true;
        }
    }

    errorRefreshToken() {
        if (this.isHubSpotComponentOpened) {
            this.onClose();
            const modalConfig: MatDialogConfig = {
                panelClass: 'modal-lg',
                data: {}
            };
            if (this.data['contacts']) {
                modalConfig.data['provider'] = OauthProvider.Hubspot;
                modalConfig.data['match_ids'] = this.exportRecords;
            }
            this.matDialog.open(ReConnectSettingComponent, modalConfig);
        }
    }

    selectLeadOwner(lid) {
        if (this.selectedLeadOwner === lid) {
            this.selectedLeadOwner = '';
        } else {
            this.selectedLeadOwner = lid;
        }
    }
    selectWorkflow(wid) {
        const idx = this.selectedWorkflows.indexOf(wid);
        if (idx === -1) {
            this.selectedWorkflows.push(wid);
        } else {
            this.selectedWorkflows.splice(idx, 1);
        }
    }
    loadWorkflows() {
        this.hubspotApiService.loadWorkflows(false).pipe(untilDestroyed(this)).subscribe((response) => {
            this.workflows = response;
            this.executeCurrentStep();
        }, (error) => {
            if (error?.status !== 500 && error?.status !== 403) {
                this.commonUtilService.showToastWithError(getErrorMessage(error));
            }
            if (error?.status === 403 || error?.status === 500) {
                this.settings.general.hsPromptForWorkflow = false;
                this.executeCurrentStep();
                return;
            }
            this.hubspotApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(() => {
                this.ngOnInit();
            }, () => {
                this.errorRefreshToken();
            });
        });
    }
    loadLeadOwner(): any {
        this.hubspotApiService.loadLeadOwner().pipe(untilDestroyed(this)).subscribe((response) => {
            this.leadOwners = response;
            this.leadOwnersLoaded = true;
            this.callWorkflow();
        }, () => {
            this.leadOwnersLoaded = true;
            this.hubspotApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(() => {
                this.ngOnInit();
            }, () => {
                this.errorRefreshToken();
            });
        });
    }
    retryExport() {
        this.currentPageIndex = this.getPageIndex(PageTypes.EXPORT);
        this.retryExports = true;
        this.executeCurrentStep();
    }
    isWorkflowSelected(wid) {
        const idx = this.selectedWorkflows.indexOf(wid);
        return idx !== -1;
    }

    executeCurrentStep() {
        this.isBusy = false;
        this.showNextButton = true;
        this.showPreviousButton = this.currentPageIndex !== this.initialPageIndex;

        if (this.currentPageIndex === this.getPageIndex(PageTypes.EXPORT_TYPE)) {
            this.showNextButton = !!this.exportType;
        }

        if (this.currentPageIndex === this.getPageIndex(PageTypes.WORKFLOW) && this.settings.general.hsPromptForLeadOwners === false) {
            this.showPreviousButton = false;
        }
        if (this.currentPageIndex === this.getPageIndex(PageTypes.DUPLICATES)) {
            if ((this.settings.general.hsPromptForWorkflow === false || this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE))
                && this.settings.general.hsPromptForLeadOwners === false) {
                this.showPreviousButton = false;
            }
            this.showPreviousButton = false;
            this.showCloseButton = false;
            this.validateDuplicates();
        }
        if (this.currentPageIndex === this.getPageIndex(PageTypes.EXPORT)) {
            this.showNextButton = false;
            this.export();
        }
        if (this.currentPageIndex === this.getPageIndex(PageTypes.EXPORT_ASYNC)) {
            this.showNextButton = false;
            this.showPreviousButton = false;
            this.exportAsync();
        }
        if (this.currentPageIndex === this.getPageIndex(PageTypes.EXPORT_FINISHED)) {
            this.showCancelButton = false;
            this.showNextButton = false;
            this.showPreviousButton = false;
            this.showCloseButton = true;
            if (this.authenticationService.hasBypassClearSelectionAfterExport() === false) {
                this.exportRecords = [];
            }
        }
        if (this.currentPageIndex === this.getPageIndex(PageTypes.EXPORT_EMPTY) && (this.data['contacts'])) {
            this.showPreviousButton = !(this.settings.general.hsPromptForLeadOwners === false && this.settings.general.hsPromptForWorkflow === false && this.settings.general.hsDuplicateResolution !== 'prompt');
        }
    }
    previous() {
        let page = this.currentPageIndex - 1;
        this.showCloseButton = false;
        if (this.currentPageIndex === this.getPageIndex(PageTypes.EXPORT_EMPTY)) {
            if (this.settings.general.hsDuplicateResolution !== 'prompt') {
                page = this.getPageIndex(PageTypes.DUPLICATES) - 1;
            } else {
                page = this.getPageIndex(PageTypes.DUPLICATES);
            }
            if (page === this.getPageIndex(PageTypes.WORKFLOW) && this.settings.general.hsPromptForWorkflow === false) {
                page = this.getPageIndex(PageTypes.LEAD_OWNER);
            }
            if (page === this.getPageIndex(PageTypes.LEAD_OWNER) && this.settings.general.hsPromptForLeadOwners === false) {
                this.showPreviousButton = false;
            }
        }

        if (this.currentPageIndex === this.getPageIndex(PageTypes.LEAD_OWNER)) {
            this.leadOwnerFilter = '';
        }
        if (this.currentPageIndex === this.getPageIndex(PageTypes.WORKFLOW)) {
            this.workFlowFilter = '';
        }
        if ((this.settings.general.hsPromptForWorkflow === false || this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE))
            && this.currentPageIndex === this.getPageIndex(PageTypes.DUPLICATES)) {
            page = page - 1;
        }
        this.currentPageIndex = page;
        this.executeCurrentStep();
    }

    next() {
        if (this.currentPageIndex === this.getPageIndex(PageTypes.LEAD_OWNER) && (this.settings.general.hsPromptForLeadOwners === false || this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE))) {
            this.currentPageIndex = this.isAsyncContactsExport() ? this.getPageIndex(PageTypes.DUPLICATES_ASYNC) : this.getPageIndex(PageTypes.DUPLICATES);
        } else {
            this.currentPageIndex++;
        }
        if (this.currentPageIndex === this.getPageIndex(PageTypes.LEAD_OWNER) && this.settings.general.hsPromptForLeadOwners === false) {
            this.currentPageIndex++;
        }
        if (this.currentPageIndex === this.getPageIndex(PageTypes.WORKFLOW) && this.settings.general.hsPromptForWorkflow === false && !this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE)) {
            this.currentPageIndex = this.currentPageIndex + 1;
            this.workFlowFilter = '';
        }
        this.executeCurrentStep();
    }

    validateDuplicates() {
        this.busyLabel = 'Validating Duplicates ...';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.isBusy = true;
        const matchIds = this.getExportRecordIds();

        this.duplicateCompanyMatches = [];
        this.duplicateContactMatches = [];

        if (this.data['contacts']) {
            this.hubspotApiService.validateDuplicates(this.exportType, matchIds, this.icpIntelJobUUID).pipe(untilDestroyed(this)).subscribe((response: any) => {
                this.duplicateContactMatches = response.contacts.filter((item: HubspotDto) => item.duplicate === true);
                this.duplicateCount = this.duplicateContactMatches.length;
                this.handleDuplicateResponse();
            }, () => this.next());
        }
        if (this.data['company']) {
            const data: HubspotCompanyRequest = {
                companyIds: matchIds,
                duplicateResolution: this.duplicateResolution,
                icpIntelJobUUID: this.icpIntelJobUUID
            }
            this.hubspotApiService.validateDuplicateCompanies(data).pipe(untilDestroyed(this)).subscribe((response: HubspotCompanyDuplicate) => {
                this.duplicateCompanyMatches = response.items.filter((item: HubspotCompany) => item.duplicate === true);
                this.duplicateCount = this.duplicateCompanyMatches.length;
                this.handleDuplicateResponse();
            }, () => this.next())
        }
    }

    handleDuplicateResponse() {
        if (this.duplicateCount === 0) {
            this.next();
        } else {
            if (this.settings.general.hsDuplicateResolution !== 'prompt') {
                this.duplicateResolution = this.settings.general.hsDuplicateResolution;
                this.showPreviousButton = false;
                this.showNextButton = false;
                this.next();
            } else {
                this.isBusy = false;
                this.showNextButton = true;
                if (this.getPageIndex(PageTypes.DUPLICATES) - 1 !== 0) {
                    if (this.data['company']) {
                        this.showPreviousButton = false;
                    } else {
                        this.showPreviousButton = !(this.settings.general.hsPromptForLeadOwners === false && this.settings.general.hsPromptForWorkflow === false);
                    }
                }
            }
        }
    }

    showWarnings() {
        this.isShowingWarnings = !this.isShowingWarnings;
    }

    private export() {
        let toBeExported = this.getExportRecordIds();
        // Skip all duplicates
        if (this.duplicateResolution === 'skip') {
            if (this.data['contacts']) {
                for (const key in this.duplicateContactMatches) {
                    if (this.duplicateContactMatches.hasOwnProperty(key)) {
                        this.commonUtilService.removeMatchId(toBeExported, this.duplicateContactMatches[key]);
                    }
                }
            }
            if (this.data['company']) {
                toBeExported = toBeExported.filter(compId => !this.duplicateCompanyMatches.some((comp) => comp.companyId === compId));
            }
        }
        if (toBeExported.length === 0) {
            this.currentPageIndex = this.getPageIndex(PageTypes.EXPORT_EMPTY);
            this.isBusy = false;
            this.showCloseButton = true;
            if (this.data['company']) {
                this.showPreviousButton = false;
            }
            return;
        }
        this.isShowingWarnings = false;
        this.warnings = [];
        this.isBusy = true;
        this.busyLabel = '';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.showPreviousButton = false;
        this.exportedContactCount = toBeExported.length;
        if (this.data['contacts']) {
        this.hubspotApiService.export(this.exportType, this.selectedLeadOwner, this.selectedWorkflows.map(Number),
            this.duplicateResolution, toBeExported, this.icpIntelJobUUID).pipe(untilDestroyed(this)).subscribe((response: any) => {
                this.requestBodyService.changeRevealMatchesID({ 'type': 'hubspot', 'data': toBeExported });
                this.warnings = response;
                this.isBusy = false;
                this.next();
                this.authenticationService.updateLoggedUserData();
            }, (error) => this.onExportFailed(error));
        }
        if (this.data['company']) {
            const exportCompaniesData: HubspotCompanyRequest = {
                companyIds: toBeExported,
                duplicateResolution: this.duplicateResolution,
                icpIntelJobUUID: this.icpIntelJobUUID
            }
            this.hubspotApiService.exportCompanies(exportCompaniesData).pipe(untilDestroyed(this)).subscribe((exportResponse: HubspotCompanyExport) => {
                this.exportedCompanyCount = exportResponse.responses.filter(el => el.exportId ? el : '').length;
                this.warnings = exportResponse.responses.filter((el:any) => !el.exportId ? { "companyName": el.companyName, "message": el.message}: '');
                this.isBusy = false;
                this.next();
                this.authenticationService.updateLoggedUserData();
            });
        }
    }

    private exportAsync(): void {
        const crmType: OfflineExportsCrmExportTypesEnum = OfflineExportsCrmExportTypesEnum.HUBSPOT;
        const request_type: OfflineExportsExportTypesEnum = this.settings.general.hsCrmStatus ? OfflineExportsExportTypesEnum.CONTACTS : OfflineExportsExportTypesEnum.LEADS;
        const job = this.commonUtilService.convertMatchIdsToRequest(this.asyncInitialData?.requestBody, this.data.contacts, { request_type });
        const body: OfflineExportsCrmExportRequestDto = {
            export_options: {
                export_settings: {
                    duplicate_resolution: this.duplicateResolution,
                },
                crm_export_options: {
                    hubspot_export_options: {
                        lifecycle_stage: this.exportType,
                        ...(this.selectedLeadOwner ? { lead_owner: this.selectedLeadOwner } : {}),
                    },
                },
            },
            job: {
                ...job,
                label: this.jobTitleControl.value || this.commonUtilService.getFileNameByRequestType(crmType),
                icpintel_job_uuid: this.icpIntelJobUUID,
                request_type,
            },
        };

        this.exportCrm(crmType, body).pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.showCloseButton = true;
                this.showCancelButton = false;
            },
            error: (error) => this.onExportFailed(error),
        });
    }

    private exportCrm(crmType: OfflineExportsCrmExportTypesEnum, body: OfflineExportsCrmExportRequestDto) {
        return this.loadingService.loadingWrapper(this.offlineExportsApiService.exportCrm(crmType, body), LOADING_TAG_ASYNC_EXPORT);
    }

    private onExportFailed(error) {
        const errMsg = getErrorMessage(error, true);
        if (errMsg) {
            this.exportError = errMsg;
            this.showRetryButton = true;
        } else {
            this.exportError = this.commonUtilService.setRevealError(error?.status, error.message);
        }
        this.currentPageIndex = this.getPageIndex(PageTypes.EXPORT_FAILED);
        this.isBusy = false;
    }

    initializeFirstStep() {
        this.isBusy = true;
        if (((this.settings.general.hsPromptForLeadOwners === false && this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE)) || (this.settings.general.hsPromptForLeadOwners === false && !this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE))) && this.currentPageIndex === this.getPageIndex(PageTypes.LEAD_OWNER)) {
            this.currentPageIndex = this.currentPageIndex + 1;
        }
        if (this.settings.general.hsPromptForLeadOwners === false && this.currentPageIndex === this.getPageIndex(PageTypes.LEAD_OWNER)) {
            this.currentPageIndex = this.currentPageIndex + 1;
        }
        if (this.settings.general.hsPromptForWorkflow === false && this.currentPageIndex === this.getPageIndex(PageTypes.WORKFLOW)) {
            this.currentPageIndex = this.currentPageIndex + 1;
        }

        this.initialPageIndex = this.currentPageIndex;
        this.hubspotApiService.validateMapping().pipe(untilDestroyed(this)).subscribe((response: any) => {
            const mappingData = response;
            if (Object.values(mappingData).every(item => item === true)) {
                if (this.settings.general.hsPromptForLeadOwners) {
                    this.loadLeadOwner();
                } else {
                    this.callWorkflow();
                }
            } else {
                let sectionsInError: string[] = [];
                if (this.userService.hasAccess(AccountAttributesEnum.TECHEXPORTS)) {
                    if (this.settings.general.hsCrmStatus) {
                        if (!mappingData.contactFields) {
                            sectionsInError.push('Contact Mappings');
                        }
                        if (!mappingData.accountFields) {
                            sectionsInError.push('Company Mappings');
                        }
                        if (!mappingData.technoAccountFields) {
                            sectionsInError.push('Technographics Mappings');
                        }
                    } else {
                        if (!mappingData.leadFields) {
                            sectionsInError.push('Lead Mappings');
                        }
                        if (!mappingData.technoLeadFields) {
                            sectionsInError.push('Technographics Mappings');
                        }
                    }
                } else {
                    if (this.settings.general.hsCrmStatus) {
                        if (!mappingData.contactFields) {
                            sectionsInError.push('Contact Mappings');
                        }
                        if (!mappingData.accountFields) {
                            sectionsInError.push('Company Mappings');
                        }
                    } else {
                        if (!mappingData.leadFields) {
                            sectionsInError.push('Lead Mappings');
                        }
                    }
                }
                sectionsInError = Array.from(new Set(sectionsInError));
                sectionsInError.forEach(section => {
                    this.commonUtilService.showToastWithError(
                        `One or more fields in your ${section} are no longer available. Update your ${section}.`
                    );
                });
                this.matDialogRef.close();
            }
        }, error => {
            this.hubspotApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(() => {
                this.initializeFirstStep();
            }, error => {
                this.errorRefreshToken();
            });
        });
    }

    onClose() {
        this.isHubSpotComponentOpened = false;
        this.matDialogRef.close();
    }

    callWorkflow() {
        if (!this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE)) {
            if (this.settings.general.hsPromptForWorkflow) {
                this.loadWorkflows();
            } else {
                this.hubspotApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(() => {
                    this.executeCurrentStep();
                }, () => {
                    this.errorRefreshToken();
                });
            }
        } else {
            this.executeCurrentStep();
        }
    }

    getExportRecordIds() {
        return this.exportRecords.map((item) => typeof item === 'object' ? item.matchId || item.companyId : item);
    }

    private isAsyncContactsExport(): boolean {
        return (this.asyncInitialData?.totalCount || this.data.contacts?.length) >= environment.hubSpotMaxCount;
    }

    private getPageIndex(pageType: PageTypes): number {
        return this.pages.findIndex(({ type }) => type === pageType);
    }
}
